import React from 'react'
import { TextInput, Forms, Form } from '@ally/forms'
import { Button, get, ButtonGroup } from '@ally/metronome-ui'
import styled from 'styled-components'
import { AccountDetails } from '../../../types'
import { useValidation } from '../../../hooks/use-validation'
import { useHostServices } from '../../../HostServices'

interface NicknameFormProps {
  account: AccountDetails
  setIsEdit: (edit: boolean) => void
  updateNickname: (accountNickname: string) => Promise<void>
  isLoading: boolean
  onCancel: () => void
}

const StyledTextInput = styled(TextInput)`
  label {
    margin-bottom: 0;
  }
`

const FormButtonWrapper = styled(ButtonGroup)`
  padding-top: ${get.space('xs')};
  padding-bottom: ${get.space('xs')};
`

export const AcctNicknameForm: React.FC<NicknameFormProps> = ({
  account,
  setIsEdit,
  updateNickname,
  isLoading,
  onCancel,
}) => {
  const { validateBadWords, validateAccountNicknameChars } = useValidation()
  const handleCancel = (): void => {
    onCancel()
    setIsEdit(false)
  }
  const { hostData } = useHostServices()

  React.useEffect(() => {
    const nicknameInput: HTMLInputElement | null = document.querySelector(
      'input#accountNickname',
    )
    /* istanbul ignore next */
    nicknameInput?.select()
  }, [])

  const handleFormSubmit = async ({
    accountNickname,
  }: {
    accountNickname: string
  }): Promise<void> => {
    // if user enters empty string then use past accountNickname
    // do empty string validation here
    if (accountNickname.length <= 0) {
      handleCancel()
    } else {
      await updateNickname(accountNickname)
      setIsEdit(false)
      hostData.afgAccounts.update()
    }
  }

  const validateNickname = (nickname: string): string => {
    if (nickname.length !== 0) {
      return (
        validateBadWords(
          nickname,
          "We've flagged one of the words you used in your response as abusive language. Try again without that word.",
        ) || validateAccountNicknameChars(nickname)
      )
    }
    return ''
  }
  return (
    <Forms
      formId="accountNicknameForm"
      initialValues={{ accountNickname: account.accountNickname }}
      onSubmit={handleFormSubmit}
    >
      <Form>
        <StyledTextInput
          labelContent=""
          name="accountNickname"
          inputType="text"
          maxLength={25}
          validate={validateNickname}
          data-testid="nicknameForm"
        />
        <FormButtonWrapper>
          <Button
            type="submit"
            text="Save"
            variant="primary"
            loading={isLoading}
            withAllyTM="editNickname-save"
          />
          {!isLoading && (
            <Button
              text="Cancel"
              variant="secondary"
              onClick={handleCancel}
              withAllyTM="editNickname-cancel"
            />
          )}
        </FormButtonWrapper>
      </Form>
    </Forms>
  )
}
